import React, { useState, useEffect } from 'react';
import { Agent, Application, Commission } from '@/api/entities';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import ProfileHeader from '../components/profile/ProfileHeader';
import ProfileDetails from '../components/profile/ProfileDetails';
import PerformanceMetrics from '../components/profile/PerformanceMetrics';

export default function AgentDetail() {
    const [agentId, setAgentId] = useState(null);
    const [agent, setAgent] = useState(null);
    const [applications, setApplications] = useState([]);
    const [commissions, setCommissions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [notes, setNotes] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        console.log('AgentDetail mounted');
        const params = new URLSearchParams(window.location.search);
        const id = params.get('id');
        console.log('Agent ID from URL:', id);
        if (id) {
            setAgentId(id);
        } else {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (agentId) {
            fetchAgentData();
        }
    }, [agentId]);

    const fetchAgentData = async () => {
        setIsLoading(true);
        setError(null);
        try {
            console.log('Fetching agent data for:', agentId);
            const agentData = await Agent.get(agentId);
            console.log('Agent data:', agentData);
            setAgent(agentData);
            setNotes(agentData.notes || '');

            const [apps, comms] = await Promise.all([
                Application.filter({ agent_id: agentId }),
                Commission.filter({ agent_id: agentId })
            ]);
            console.log('Applications:', apps?.length || 0);
            console.log('Commissions:', comms?.length || 0);
            setApplications(apps || []);
            setCommissions(comms || []);
        } catch (error) {
            console.error("Failed to fetch agent details:", error);
            setError(error.message || 'Failed to load agent details');
            setApplications([]);
            setCommissions([]);
        }
        setIsLoading(false);
    };

    const handleUpdateNotes = async () => {
        if (!agentId) return;
        await Agent.update(agentId, { notes });
        alert('Notes updated successfully!');
    };

    const handleStatusChange = async (newStatus) => {
        if (!agentId) return;
        await Agent.update(agentId, { status: newStatus });
        fetchAgentData(); // Refresh data
    };
    
    if (isLoading) return (
        <div className="p-6 space-y-8 bg-gray-200 min-h-screen">
          <div className="max-w-7xl mx-auto">
            <Skeleton className="h-48 w-full rounded-3xl mb-8" />
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <Skeleton className="h-64 w-full rounded-3xl" />
              </div>
              <div className="space-y-8">
                <Skeleton className="h-56 w-full rounded-3xl" />
              </div>
            </div>
          </div>
        </div>
      );

    if (error) return (
        <div className="p-6 bg-gray-200 min-h-screen flex items-center justify-center">
            <Card className="neumorphic rounded-3xl p-8 max-w-md">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Agent</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <Button onClick={() => window.location.reload()} className="neumorphic rounded-xl">
                        Try Again
                    </Button>
                </div>
            </Card>
        </div>
    );

    if (!agent && !isLoading) return (
        <div className="p-6 bg-gray-200 min-h-screen flex items-center justify-center">
            <Card className="neumorphic rounded-3xl p-8 max-w-md">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-neumorphic mb-4">Agent Not Found</h2>
                    <p className="text-gray-600 mb-4">Could not find the specified agent. Please check the ID and try again.</p>
                    <Button onClick={() => window.history.back()} className="neumorphic rounded-xl">
                        Go Back
                    </Button>
                </div>
            </Card>
        </div>
    );

    return (
        <div className="p-6 space-y-8 bg-gray-200 min-h-screen">
            <div className="max-w-7xl mx-auto">
                <div className="grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-8">
                        <ProfileHeader agent={agent} onPhotoUpload={fetchAgentData} />
                        <PerformanceMetrics metrics={agent.performance_metrics} />
                        <Card className="neumorphic rounded-3xl">
                            <CardHeader>
                                <CardTitle className="text-lg font-semibold text-neumorphic">Submitted Applications ({applications.length})</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {applications.slice(0, 5).map(app => (
                                    <div key={app.id} className="neumorphic-inset p-3 rounded-xl text-sm">
                                        {app.student_name} - {app.program_applied} ({app.status})
                                    </div>
                                ))}
                                {applications.length === 0 && <p className="text-sm text-gray-500">No applications submitted yet.</p>}
                            </CardContent>
                        </Card>

                        <Card className="neumorphic rounded-3xl">
                            <CardHeader>
                                <CardTitle className="text-lg font-semibold text-neumorphic">Commissions ({commissions?.length || 0})</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {commissions && commissions.length > 0 ? (
                                    <>
                                        {commissions.slice(0, 5).map(comm => (
                                            <div key={comm.id} className="neumorphic-inset p-4 rounded-xl">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="text-sm font-medium text-neumorphic">{comm.student_name || 'N/A'}</div>
                                                    <Badge className={
                                                        comm.status === 'paid' ? 'bg-green-500' :
                                                        comm.status === 'approved' ? 'bg-blue-500' :
                                                        comm.status === 'disputed' ? 'bg-red-500' :
                                                        'bg-gray-500'
                                                    }>
                                                        {comm.status || 'pending'}
                                                    </Badge>
                                                </div>
                                                <div className="text-xs text-gray-600 mb-1">{comm.program || 'N/A'}</div>
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="text-gray-500">Commission:</span>
                                                    <span className="font-semibold text-blue-600">
                                                        ${Number(comm.commission_amount || 0).toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                        {commissions.length > 5 && (
                                            <p className="text-xs text-gray-500 text-center pt-2">
                                                Showing 5 of {commissions.length} commissions
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <p className="text-sm text-gray-500">No commissions recorded yet.</p>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                    <div className="space-y-8">
                        <ProfileDetails agent={agent} />
                         <Card className="neumorphic rounded-3xl">
                            <CardHeader>
                                <CardTitle className="text-lg font-semibold text-neumorphic">Internal Notes</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <Textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="neumorphic-inset rounded-xl border-0 bg-gray-200"
                                    rows={5}
                                    placeholder="Add internal comments here..."
                                />
                                <Button onClick={handleUpdateNotes} className="neumorphic rounded-xl w-full">Save Notes</Button>
                            </CardContent>
                        </Card>
                        <Card className="neumorphic rounded-3xl">
                            <CardHeader>
                                <CardTitle className="text-lg font-semibold text-neumorphic">Agent Status</CardTitle>
                            </CardHeader>
                            <CardContent className="flex flex-wrap gap-2">
                                <Button onClick={() => handleStatusChange('active')} disabled={agent.status === 'active'} className="neumorphic-active disabled:opacity-50">Activate</Button>
                                <Button onClick={() => handleStatusChange('suspended')} disabled={agent.status === 'suspended'} className="bg-red-500 text-white neumorphic">Suspend</Button>
                                <Button onClick={() => handleStatusChange('pending')} disabled={agent.status === 'pending'}>Set to Pending</Button>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}